/**
 * OAuth Manager
 * Framework-agnostic core OAuth logic
 * Zero dependencies on React or any UI framework
 */

import type {
  Platform,
  OAuthConnection,
  OAuthConnectionStatus,
  OAuthCallbackResult,
  OAuthStateListener,
} from './types';
import { OAuthApiClient } from './OAuthApiClient';
import { OAuthStore } from './OAuthStore';
import { OAuthStorage } from './storage';

export class OAuthManager {
  private apiClient: OAuthApiClient;
  private store: OAuthStore;
  private defaultRedirectUri: string;

  /**
   * Initialize OAuth Manager
   *
   * @param apiClient - Optional custom API client
   * @param store - Optional custom store
   * @param redirectUri - Default OAuth callback URI
   */
  constructor(
    apiClient?: OAuthApiClient,
    store?: OAuthStore,
    redirectUri: string = `${window.location.origin}/oauth/callback`
  ) {
    this.apiClient = apiClient || new OAuthApiClient();
    this.store = store || new OAuthStore();
    this.defaultRedirectUri = redirectUri;
  }

  /**
   * Subscribe to connection state changes
   *
   * @param listener - Callback invoked when connections change
   * @returns Unsubscribe function
   */
  subscribe(listener: OAuthStateListener): () => void {
    return this.store.subscribe(listener);
  }

  /**
   * Initiate OAuth flow for a platform
   * Stores state in localStorage and redirects to platform
   *
   * @param platform - Platform to connect
   * @param returnUrl - Optional URL to return to after OAuth completes
   * @returns Authorization URL (for testing/custom redirect logic)
   */
  async initiate(platform: Platform, returnUrl?: string): Promise<string> {
    try {
      this.store.setLoading(true);
      this.store.setError(null);

      // Store current location to return to after OAuth
      if (returnUrl) {
        OAuthStorage.setRedirectUrl(returnUrl);
      } else {
        OAuthStorage.setRedirectUrl(window.location.pathname);
      }

      // Initiate OAuth flow
      const response = await this.apiClient.initiate(platform, this.defaultRedirectUri);

      // Store state for CSRF verification
      OAuthStorage.setState(response.state);

      // Redirect to platform authorization page
      window.location.href = response.authorization_url;

      return response.authorization_url;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to initiate OAuth';
      this.store.setError(errorMessage);
      throw error;
    } finally {
      this.store.setLoading(false);
    }
  }

  /**
   * Handle OAuth callback after platform redirects back
   * Verifies state and exchanges code for tokens
   *
   * SECURITY NOTE: State validation is performed by the backend using
   * the oauth_states database table. Frontend localStorage validation
   * is optional (as a sanity check) since localStorage can be cleared
   * during org switches or browser navigation.
   *
   * @param code - Authorization code from URL params
   * @param state - State parameter from URL params
   * @returns Callback result with connection info
   */
  async handleCallback(code: string, state: string): Promise<OAuthCallbackResult> {
    try {
      this.store.setLoading(true);
      this.store.setError(null);

      // Optional: Check localStorage state as sanity check
      // But DON'T fail if missing - backend will validate properly
      const storedState = OAuthStorage.getAndClearState();
      if (storedState && state !== storedState) {
        console.warn(
          'OAuth state mismatch in localStorage. ' +
          'Backend will perform authoritative validation.'
        );
      }

      // Exchange code for tokens
      // Backend validates state using oauth_states table (authoritative)
      const result = await this.apiClient.callback(code, state);

      if (!result.success) {
        const errorMessage = result.error || 'OAuth callback failed';

        // Provide helpful error messages based on error type
        if (errorMessage.includes('state')) {
          throw new Error(
            'OAuth session expired or invalid. ' +
            'This can happen if you switched organizations during login. ' +
            'Please restart the connection process.'
          );
        } else if (errorMessage.includes('expired')) {
          throw new Error(
            'OAuth session expired (timeout after 10 minutes). ' +
            'Please restart the connection process.'
          );
        } else {
          throw new Error(errorMessage);
        }
      }

      // Refresh connections list
      await this.getConnections();

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'OAuth callback failed';
      this.store.setError(errorMessage);
      throw error;
    } finally {
      this.store.setLoading(false);
    }
  }

  /**
   * Get redirect URL stored before OAuth flow
   * Clears the stored value after retrieval
   *
   * @returns Redirect URL or null
   */
  getRedirectUrl(): string | null {
    return OAuthStorage.getAndClearRedirectUrl();
  }

  /**
   * Fetch all OAuth connections for current user
   * Updates store with results
   *
   * @param platform - Optional filter by platform
   * @returns Array of connections
   */
  async getConnections(platform?: Platform): Promise<OAuthConnection[]> {
    try {
      this.store.setLoading(true);
      this.store.setError(null);

      const connections = await this.apiClient.getConnections(platform);
      this.store.setConnections(connections);

      return connections;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch connections';
      this.store.setError(errorMessage);
      return [];
    } finally {
      this.store.setLoading(false);
    }
  }

  /**
   * Get connection for specific platform (non-reactive)
   *
   * @param platform - Platform to get connection for
   * @returns Connection or null
   */
  getConnection(platform: Platform): OAuthConnection | null {
    return this.store.getConnection(platform);
  }

  /**
   * Check connection status for a platform
   *
   * @param platform - Platform to check
   * @returns Connection status
   */
  async getStatus(platform: Platform): Promise<OAuthConnectionStatus> {
    try {
      return await this.apiClient.getStatus(platform);
    } catch (error) {
      return {
        connected: false,
        platform,
        error: error instanceof Error ? error.message : 'Failed to check status',
      };
    }
  }

  /**
   * Disconnect/revoke an OAuth connection
   *
   * @param connectionId - Connection ID to revoke
   */
  async disconnect(connectionId: string): Promise<void> {
    try {
      this.store.setLoading(true);
      this.store.setError(null);

      await this.apiClient.disconnect(connectionId);

      // Remove from store
      this.store.removeConnection(connectionId);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to disconnect';
      this.store.setError(errorMessage);
      throw error;
    } finally {
      this.store.setLoading(false);
    }
  }

  /**
   * Get list of supported platforms
   *
   * @returns Array of platform identifiers
   */
  async getPlatforms(): Promise<Platform[]> {
    try {
      return await this.apiClient.getPlatforms();
    } catch (error) {
      console.error('Failed to fetch available platforms:', error);
      return [];
    }
  }

  /**
   * Check if platform is connected
   *
   * @param platform - Platform to check
   * @returns True if connected and not expired
   */
  isConnected(platform: Platform): boolean {
    const connection = this.store.getConnection(platform);
    return connection !== null && !connection.is_expired;
  }

  /**
   * Get loading state
   */
  isLoading(): boolean {
    return this.store.isLoading();
  }

  /**
   * Get error state
   */
  getError(): string | null {
    return this.store.getError();
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.store.setError(null);
  }
}

// Singleton instance for convenience
let _oauthManager: OAuthManager | null = null;

/**
 * Get singleton OAuth Manager instance
 *
 * @returns OAuth Manager instance
 */
export function getOAuthManager(): OAuthManager {
  if (!_oauthManager) {
    _oauthManager = new OAuthManager();
  }
  return _oauthManager;
}
